import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Modal } from "@/components/Modals/Modal";

interface GenerationConflictDialogProps {
	/**
	 * "in-flight": a QUEUED/RUNNING job from a different signal — info only,
	 * there is no cancel API (D5 deviation from the mockup's "stop it").
	 * "ready": unreviewed COMPLETED drafts from a different signal — replace?
	 */
	variant: "in-flight" | "ready";
	personFirstName: string;
	onClose: () => void;
	/** "ready" only: fire a fresh generate seeded by the new signal (D5.2). */
	onReplace: () => void;
}

/**
 * Pre-flight conflict prompt of the per-signal AI CTA (PREC-3635 P6 D5).
 * Deliberate deviation from the mockup's `confirm("Stop it …")`: P3 shipped no
 * cancel endpoint, so an in-flight generation can only be kept — never stopped.
 */
export const GenerationConflictDialog: FunctionComponent<GenerationConflictDialogProps> = ({
	variant,
	personFirstName,
	onClose,
	onReplace,
}) => {
	const { t } = useTranslation();

	return (
		<Modal
			footer={
				variant === "in-flight" ? (
					<div className="flex w-full justify-end">
						<Button
							size={ButtonSize.S}
							title={t("feed.conflict.inFlightOk")}
							type="button"
							onClick={onClose}
						/>
					</div>
				) : (
					<div className="flex w-full items-center justify-end gap-2.5">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.S}
							title={t("feed.conflict.readyKeep")}
							type="button"
							onClick={onClose}
						/>
						<Button
							size={ButtonSize.S}
							title={t("feed.conflict.readyReplace")}
							type="button"
							onClick={onReplace}
						/>
					</div>
				)
			}
			handleClose={onClose}
			isOpened
			size="xs"
			title={
				variant === "in-flight" ? t("feed.conflict.inFlightTitle") : t("feed.conflict.readyTitle")
			}
			titleWeight="bold"
		>
			<p className="text-[13px] leading-relaxed text-text" data-testid="feed-conflict-body">
				{variant === "in-flight"
					? t("feed.conflict.inFlightBody", { name: personFirstName })
					: t("feed.conflict.readyBody", { name: personFirstName })}
			</p>
		</Modal>
	);
};
